import { computed, reactive } from "vue";
import courseSource from "../../content/courses.json";
import actionSource from "../../content/actions.json";
import updateSource from "../../content/updates.json";

export type Course = {
  id: string;
  title: string;
  weekday: number;
  period: string;
  time: string;
  room?: string;
  teacher?: string;
  section?: string;
  weeks?: string;
  color?: string;
  relatedIds?: string[];
};
export type PersonalCourse = {
  name: string;
  section: string | null;
  teacher: string | null;
  slots: Array<{
    weekday: number;
    periodStart: number;
    periodEnd: number;
    weeks: {
      start: number;
      end: number;
      mode: "all" | "odd" | "even";
      exclude: number[];
    };
    location: string | null;
  }>;
};
type Item = {
  id?: string;
  kind: "event" | "opening" | "deadline" | "task";
  title: string;
  start: string | null;
  end: string | null;
  due?: string | null;
  location: string | null;
  notes: string | null;
  source: string;
  links?: Array<{ title: string; url: string }>;
  bring?: string[];
};
type User = { id: string; name: string; roles: string[]; groups: string[] };
type PrivateData = {
  schemaVersion: "1.0";
  subscriptions: string[];
  courses: PersonalCourse[];
  items: Item[];
  actionStates: Array<Record<string, unknown>>;
  settings: Record<string, any>;
  updatedAt: string | null;
};
export type ArchivedNotice = {
  id: string;
  title: string;
  rawText: string;
  source: string;
  receivedAt: string;
  savedAt: string;
  savedBy: string;
};
type TaskRow = Record<string, any> & {
  id: string;
  title: string;
  origin: string;
  done: boolean;
};
type ScheduledRow = Record<string, any> & {
  id: string;
  title: string;
  scheduledAt: string;
  scheduledEndAt?: string | null;
};

const freshData = (): PrivateData => ({
  schemaVersion: "1.0",
  subscriptions: [],
  courses: [],
  items: [],
  actionStates: [],
  settings: {},
  updatedAt: null,
});
// Results from an earlier account load must never repopulate a logged-out session.
let accountLoadEpoch = 0;
const state = reactive({
  initialized: false,
  busy: false,
  error: "",
  noticeError: "",
  user: null as User | null,
  data: freshData(),
  revision: null as string | null,
  assignedItems: [] as Item[],
  assignedCourseOverrides: {} as Record<string, Partial<Course>>,
  notices: [] as ArchivedNotice[],
  noticeRevision: null as string | null,
  privateDataLoaded: false,
  courseAssignmentsLoaded: false,
  assignmentsLoaded: false,
  syncStatus: "idle" as "idle" | "loading" | "ready" | "partial",
});
const publicCourses = courseSource as Course[];
const publicActions = actionSource as Array<Record<string, any>>;
export const updates = updateSource as Array<Record<string, any>>;
export const store = state;
export const courses = computed(() => {
  const hidden = new Set<string>(
    Array.isArray(state.data.settings.hiddenCourseIds)
      ? state.data.settings.hiddenCourseIds
      : [],
  );
  const edits =
    state.data.settings.courseOverrides &&
    typeof state.data.settings.courseOverrides === "object"
      ? (state.data.settings.courseOverrides as Record<string, PersonalCourse>)
      : {};
  const base = publicCourses
    .filter((course) => !hidden.has(course.id))
    .map((course) => {
      const assigned = state.assignedCourseOverrides[course.id];
      const effective = assigned ? { ...course, ...assigned, id: course.id } : course;
      return edits[course.id]
        ? toDisplay(edits[course.id], `edit:${course.id}`, effective)
        : effective;
    });
  return [
    ...base,
    ...state.data.courses.flatMap((course, index) =>
      course.slots.map((slot, slotIndex) =>
        personalDisplay(course, slot, `mine:${index}:${slotIndex}`),
      ),
    ),
  ];
});
export const tasks = computed<TaskRow[]>(() => {
  const publicItems = publicActions.filter(
    (item) => item.kind === "task",
  ) as TaskRow[];
  const ownItems = state.data.items
    .filter((item) => item.kind !== "event")
    .map((item) => ({
      id: item.id ? `personal:${item.id}` : `mine:${item.title}`,
      title: item.title,
      deadlineAt: item.due || item.start || null,
      scheduledAt: item.kind === "opening" ? item.start : null,
      location: item.location || "",
      summary: item.notes || "",
      instructions: item.notes || "",
      bring: item.bring || [],
      origin: "我的待办",
      links: (item.links || []).map((link) => ({
        title: link.title,
        url: link.url,
      })),
    }));
  const assigned = state.assignedItems
    .filter((item) => item.kind !== "event")
    .map((item) => ({
      id: `assigned:${item.id}`,
      title: item.title,
      deadlineAt: item.due || item.start || null,
      scheduledAt: item.kind === "opening" ? item.start : null,
      location: item.location || "",
      summary: item.notes || "",
      instructions: item.notes || "",
      bring: item.bring || [],
      origin: item.source || "班级定向事项",
      links: (item.links || []).map((link) => ({
        title: link.title,
        url: link.url,
      })),
    }));
  const combined: TaskRow[] = [
    ...publicItems.map((item) => ({ ...item, origin: "班级事项" })),
    ...assigned,
    ...ownItems,
  ] as TaskRow[];
  return combined.map((item) => ({
    ...item,
    personalNote: String(state.data.actionStates.find((saved) => saved.actionId === item.id)?.note || ""),
    personalBring: actionStateBring(state.data.actionStates.find((saved) => saved.actionId === item.id)),
    done: state.data.actionStates.some(
      (saved) => saved.actionId === item.id && Boolean(saved.completedAt),
    ),
  }));
});
export const scheduledEvents = computed<ScheduledRow[]>(() => {
  const publicItems = publicActions.filter(
    (item) => item.kind === "schedule" && item.scheduledAt,
  ) as ScheduledRow[];
  const assigned = state.assignedItems
    .filter((item) => item.kind === "event" && item.start)
    .map((item) => ({
      id: `assigned:${item.id}`,
      title: item.title,
      scheduledAt: item.start!,
      scheduledEndAt: item.end,
      location: item.location || "",
      summary: item.notes || "",
      origin: item.source || "班级日程",
      instructions: item.notes || "",
      bring: item.bring || [],
      links: item.links || [],
    }));
  const personal = state.data.items
    .filter((item) => item.kind === "event" && item.start)
    .map((item) => ({
      id: `personal:${item.id || item.title}`,
      title: item.title,
      scheduledAt: item.start!,
      scheduledEndAt: item.end,
      location: item.location || "",
      summary: item.notes || "",
      origin: item.source || "我的日程",
      instructions: item.notes || "",
      bring: item.bring || [],
      links: item.links || [],
    }));
  return [...publicItems, ...assigned, ...personal] as ScheduledRow[];
});

export async function initialize() {
  if (state.initialized || state.busy) return;
  state.busy = true;
  try {
    const response = await fetch("/api/me", { credentials: "same-origin" });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result?.ok) {
      state.error =
        response.status >= 500 ? "账号服务暂不可用，个人数据无法同步。" : "";
      return;
    }
    if (response.ok && result.authenticated && result.user) {
      state.user = result.user;
      await loadAllUserData();
    } else {
      state.syncStatus = "idle";
    }
  } catch {
    state.error = "账号服务暂不可用，个人数据无法同步。";
  } finally {
    state.busy = false;
    state.initialized = true;
  }
}

export async function login(identity: string, key: string) {
  state.busy = true;
  state.error = "";
  try {
    const response = await fetch("/api/auth/login", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        identity: identity.trim(),
        key: key.replace(/[\s\u200B-\u200D\uFEFF]/g, ""),
      }),
    });
    const result = await response.json();
    if (!response.ok || !result.authenticated)
      throw new Error(
        result.error === "invalid_credentials"
          ? "姓名或密码不正确。"
          : "登录暂不可用，请稍后重试。",
      );
    state.user = result.user;
    state.data = freshData();
    state.revision = null;
    await loadAllUserData();
    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : "登录失败。";
    return false;
  } finally {
    state.busy = false;
    state.initialized = true;
  }
}

export async function logout() {
  await fetch("/api/auth/logout", {
    method: "POST",
    credentials: "same-origin",
  }).catch(() => undefined);
  accountLoadEpoch++;
  state.user = null;
  state.data = freshData();
  state.revision = null;
  state.assignedItems = [];
  state.assignedCourseOverrides = {};
  state.notices = [];
  state.noticeRevision = null;
  state.noticeError = "";
  state.error = "";
  state.privateDataLoaded = false;
  state.courseAssignmentsLoaded = false;
  state.assignmentsLoaded = false;
  state.syncStatus = "idle";
}

async function loadAllUserData() {
  const epoch = ++accountLoadEpoch;
  state.syncStatus = "loading";
  state.privateDataLoaded = false;
  state.courseAssignmentsLoaded = false;
  state.assignmentsLoaded = false;
  const pendingNotices = loadNotices(epoch);
  const results = await Promise.allSettled([
    loadPrivateData(epoch), loadCourseAssignments(epoch), loadAssignments(epoch),
  ]);
  // Notices are optional for the next-action screen. Do not wait for that archive.
  void pendingNotices;
  if (epoch !== accountLoadEpoch) return;
  state.syncStatus = results[0].status === "fulfilled"
    && state.privateDataLoaded && state.courseAssignmentsLoaded && state.assignmentsLoaded
    ? "ready" : "partial";
  if (state.syncStatus === "partial") {
    state.error = "部分个人数据暂未同步，请勿将当前课表当作完整安排。";
  }
}

async function loadPrivateData(epoch = accountLoadEpoch) {
  const response = await fetch("/api/me/data", {
    credentials: "same-origin",
    cache: "no-store",
  });
  const result = await response.json();
  if (!response.ok || !result.ok) throw new Error("个人数据暂时无法读取。");
  if (epoch !== accountLoadEpoch) return;
  state.data = result.data;
  state.revision = result.revision || null;
  state.privateDataLoaded = true;
}

async function loadCourseAssignments(epoch = accountLoadEpoch) {
  try {
    const response = await fetch("/api/me/course-assignments", {
      credentials: "same-origin",
      cache: "no-store",
    });
    const result = await response.json();
    if (epoch !== accountLoadEpoch) return;
    if (response.ok && result.ok && result.overrides && typeof result.overrides === "object") {
      state.assignedCourseOverrides = result.overrides as Record<string, Partial<Course>>;
      state.courseAssignmentsLoaded = true;
      return;
    }
  } catch {
    // Fall back to the public timetable when personalized assignment data is unavailable.
  }
  if (epoch === accountLoadEpoch) {
    state.assignedCourseOverrides = {};
    state.courseAssignmentsLoaded = false;
  }
}

async function loadAssignments(epoch = accountLoadEpoch) {
  try {
    const response = await fetch("/api/me/assignments", {
      credentials: "same-origin",
      cache: "no-store",
    });
    const result = await response.json();
    if (!response.ok || !result.ok || !Array.isArray(result.items)) throw new Error("assigned_actions_unavailable");
    if (epoch !== accountLoadEpoch) return;
    state.assignedItems = result.items as Item[];
    state.assignmentsLoaded = true;
  } catch {
    if (epoch === accountLoadEpoch) {
      state.assignedItems = [];
      state.assignmentsLoaded = false;
    }
  }
}

export async function refreshSharedData() {
  if (!state.user) return;
  await Promise.allSettled([loadCourseAssignments(), loadAssignments(), loadNotices()]);
  state.syncStatus = state.privateDataLoaded && state.courseAssignmentsLoaded && state.assignmentsLoaded
    ? "ready" : "partial";
}

export async function save(next: PrivateData) {
  if (!state.user) {
    state.error = "登录后才能保存个人课表和待办。";
    return false;
  }
  if (!state.privateDataLoaded) {
    state.error = "个人数据尚未完整载入，已阻止覆盖保存，请稍后重试。";
    return false;
  }
  state.busy = true;
  state.error = "";
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), 30_000);
  try {
    const response = await fetch("/api/me/data", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ revision: state.revision, data: next }),
      signal: controller.signal,
    });
    const result = await response.json();
    if (response.status === 409) {
      await loadPrivateData();
      throw new Error("其他设备刚更新了你的数据，已重新载入；请再保存一次。");
    }
    if (!response.ok || !result.ok) {
      if (response.status === 401) throw new Error("登录已失效，请重新登录后保存。");
      throw new Error(`保存失败（${String(result?.error || response.status)}），请稍后重试。`);
    }
    state.data = {
      ...next,
      updatedAt: result.updatedAt || new Date().toISOString(),
    };
    state.revision = result.revision;
    return true;
  } catch (error) {
    state.error = controller.signal.aborted
      ? "保存超时，无法确认是否已写入。请刷新课表核对后再试。"
      : error instanceof Error ? error.message : "保存失败。";
    return false;
  } finally {
    globalThis.clearTimeout(timeout);
    state.busy = false;
  }
}

async function loadNotices(epoch = accountLoadEpoch) {
  try {
    const response = await fetch("/api/notices", {
      credentials: "same-origin",
      cache: "no-store",
    });
    const result = await response.json();
    if (!response.ok || !result.ok || !Array.isArray(result.notices))
      throw new Error("通知档案暂时无法读取，请稍后再试。");
    if (epoch !== accountLoadEpoch) return;
    state.notices = result.notices;
    state.noticeRevision = result.revision || null;
    state.noticeError = "";
  } catch (error) {
    if (epoch === accountLoadEpoch) state.noticeError =
      error instanceof Error ? error.message : "通知档案暂时无法读取。";
  }
}

export async function archiveNotice(
  title: string,
  rawText: string,
  source: string,
  receivedAt: string,
) {
  if (!state.user) {
    state.error = "登录后才能将通知存入全班原文档案。";
    return false;
  }
  state.busy = true;
  state.error = "";
  try {
    const response = await fetch("/api/notices", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, rawText, source, receivedAt }),
    });
    const result = await response.json();
    if (!response.ok || !result.ok)
      throw new Error(
        result.error === "stale_revision"
          ? "档案刚被其他同学更新，请重新提交。"
          : "原文归档失败，请稍后重试。",
      );
    state.notices = result.notices;
    state.noticeRevision = result.revision;
    state.noticeError = "";
    return true;
  } catch (error) {
    state.error = error instanceof Error ? error.message : "原文归档失败。";
    return false;
  } finally {
    state.busy = false;
  }
}

export async function setTaskDone(id: string, done: boolean) {
  const previous = state.data.actionStates.find((item) => item.actionId === id);
  const next = state.data.actionStates.filter((item) => item.actionId !== id);
  if (done || previous) {
    const updatedAt = new Date().toISOString();
    next.push({
      ...(previous || {}),
      actionId: id,
      ...(done ? { completedAt: updatedAt } : { completedAt: null }),
      updatedAt,
    });
  }
  return save({ ...state.data, actionStates: next });
}

function actionStateBring(saved?: Record<string, unknown>) {
  const blocks = Array.isArray(saved?.blocks) ? saved.blocks : [];
  return blocks
    .filter((block): block is Record<string, unknown> => Boolean(block) && typeof block === "object")
    .filter((block) => block.type === "bring")
    .map((block) => String(block.text || "").trim())
    .filter(Boolean);
}

export async function saveEntryMemo(actionId: string, note: string, bringText: string) {
  const previous = state.data.actionStates.find((item) => item.actionId === actionId);
  const blocks = Array.isArray(previous?.blocks) ? previous.blocks : [];
  const preserved = blocks.filter(
    (block) => !block || typeof block !== "object" || (block as Record<string, unknown>).type !== "bring",
  );
  const timestamp = new Date().toISOString();
  const bringBlocks = bringText
    .split(/[\n,，、;]/)
    .map((text) => text.trim())
    .filter(Boolean)
    .slice(0, 30)
    .map((text) => ({
      id: `bring:${crypto.randomUUID()}`,
      type: "bring",
      text,
      checked: false,
      createdAt: timestamp,
      updatedAt: timestamp,
    }));
  const nextState = {
    ...(previous || {}),
    actionId,
    note: note.trim().slice(0, 2000),
    blocks: [...preserved, ...bringBlocks],
    updatedAt: timestamp,
  };
  const actionStates = state.data.actionStates.filter((item) => item.actionId !== actionId);
  actionStates.push(nextState);
  return save({ ...state.data, actionStates });
}

export async function addTask(title: string, due: string) {
  const item: Item = {
    id: `mine:${crypto.randomUUID()}`,
    kind: "task",
    title: title.trim(),
    start: null,
    end: null,
    due: due ? new Date(due).toISOString() : null,
    location: null,
    notes: null,
    source: "我添加的待办",
  };
  return save({ ...state.data, items: [...state.data.items, item] });
}

export async function saveEvent(
  title: string,
  start: string,
  end: string,
  location: string,
  notes: string,
  id?: string,
) {
  const event: Item = {
    id: id || crypto.randomUUID(),
    kind: "event",
    title: title.trim(),
    start: new Date(start).toISOString(),
    end: end ? new Date(end).toISOString() : null,
    due: null,
    location: location.trim() || null,
    notes: notes.trim() || null,
    source: "我的日程",
  };
  const items = id
    ? state.data.items.map((item) => (item.id === id ? event : item))
    : [...state.data.items, event];
  return save({ ...state.data, items });
}

export async function deleteEvent(id: string) {
  return save({
    ...state.data,
    items: state.data.items.filter((item) => item.id !== id),
  });
}

export async function saveCourse(course: PersonalCourse, publicId?: string) {
  if (!publicId)
    return save({ ...state.data, courses: [...state.data.courses, course] });
  const edits = {
    ...(state.data.settings.courseOverrides || {}),
    [publicId]: course,
  };
  return save({
    ...state.data,
    settings: {
      ...state.data.settings,
      courseOverrides: edits,
      hiddenCourseIds: (state.data.settings.hiddenCourseIds || []).filter(
        (id: string) => id !== publicId,
      ),
    },
  });
}

function clonePersonalCourses(): PersonalCourse[] {
  // These records are JSON data. Serializing unwraps Vue's nested proxies too.
  return JSON.parse(JSON.stringify(state.data.courses)) as PersonalCourse[];
}

export async function updatePersonalCourse(
  courseId: string,
  course: PersonalCourse,
) {
  const [, courseIndex, slotIndex] = courseId
    .split(":")
    .map((value, index) => (index === 0 ? value : Number(value)));
  const next = clonePersonalCourses();
  const target = next[courseIndex as number];
  if (!target?.slots[slotIndex as number]) return false;
  target.name = course.name;
  target.teacher = course.teacher;
  target.slots[slotIndex as number] = course.slots[0];
  return save({ ...state.data, courses: next });
}

export async function removeCourse(course: Course) {
  if (course.id.startsWith("edit:")) {
    const id = course.id.slice(5);
    const edits = { ...(state.data.settings.courseOverrides || {}) };
    delete edits[id];
    return save({
      ...state.data,
      settings: {
        ...state.data.settings,
        courseOverrides: edits,
        hiddenCourseIds: (state.data.settings.hiddenCourseIds || []).filter(
          (value: string) => value !== id,
        ),
      },
    });
  }
  if (course.id.startsWith("mine:")) {
    const [, courseIndex, slotIndex] = course.id
      .split(":")
      .map((value, index) => (index === 0 ? value : Number(value)));
    const next = clonePersonalCourses();
    const target = next[courseIndex as number];
    target?.slots.splice(slotIndex as number, 1);
    const compact = next.filter((item) => item.slots.length > 0);
    return save({ ...state.data, courses: compact });
  }
  return saveCourse(toPersonal(course), course.id);
}

export function courseToPersonal(
  course: Course,
  weekday = course.weekday,
  start?: number,
  end?: number,
): PersonalCourse {
  const periods = course.period.match(/\d+/g)?.map(Number) || [1, 1];
  return toPersonal(course, weekday, start || periods[0], end || periods[1]);
}

function toPersonal(
  course: Course,
  weekday = course.weekday,
  periodStart?: number,
  periodEnd?: number,
): PersonalCourse {
  const periods = course.period.match(/\d+/g)?.map(Number) || [1, 1];
  const weekText = course.weeks || "1–18";
  const ranges = Array.from(weekText.matchAll(/(\d+)[–-](\d+)/g)).map(
    (match) => [Number(match[1]), Number(match[2])] as const,
  );
  const start = ranges.length
    ? Math.min(...ranges.map((range) => range[0]))
    : Number(weekText.match(/第(\d+)周起/)?.[1] || 1);
  const end = ranges.length ? Math.max(...ranges.map((range) => range[1])) : 18;
  const mode = /单周/.test(course.weeks || "")
    ? "odd"
    : /双周/.test(course.weeks || "")
      ? "even"
      : "all";
  const weeksInsideRanges = new Set(
    ranges.flatMap(([from, to]) =>
      Array.from(
        { length: Math.max(0, to - from + 1) },
        (_, index) => from + index,
      ),
    ),
  );
  const missingWeeks = ranges.length
    ? Array.from(
        { length: Math.max(0, end - start + 1) },
        (_, index) => start + index,
      ).filter((week) => !weeksInsideRanges.has(week))
    : [];
  const explicitExclusions =
    weekText
      .match(/除(\d+(?:、\d+)*)周/)?.[1]
      .split("、")
      .map(Number) || [];
  const exclude = Array.from(new Set([...missingWeeks, ...explicitExclusions]));
  return {
    name: course.title,
    section: null,
    teacher: course.teacher || null,
    slots: [
      {
        weekday,
        periodStart: periodStart || periods[0],
        periodEnd: periodEnd || periods[1],
        weeks: { start, end, mode, exclude },
        location: course.room || null,
      },
    ],
  };
}

function toDisplay(course: PersonalCourse, id: string, base: Course): Course {
  return {
    ...base,
    id,
    title: course.name,
    weekday: course.slots[0].weekday,
    period: `${course.slots[0].periodStart}–${course.slots[0].periodEnd}`,
    time: periodTime(course.slots[0].periodStart, course.slots[0].periodEnd),
    room: course.slots[0].location || undefined,
    teacher: course.teacher || undefined,
    weeks: formatWeeks(course.slots[0].weeks),
  };
}
function personalDisplay(
  course: PersonalCourse,
  slot: PersonalCourse["slots"][number],
  id: string,
): Course {
  return {
    id,
    title: course.name,
    weekday: slot.weekday,
    period: `${slot.periodStart}–${slot.periodEnd}`,
    time: periodTime(slot.periodStart, slot.periodEnd),
    room: slot.location || undefined,
    teacher: course.teacher || undefined,
    weeks: formatWeeks(slot.weeks),
    color: "red",
  };
}
export function periodTime(start: number, end: number) {
  const startTimes = [
    "",
    "08:50",
    "09:40",
    "10:40",
    "11:30",
    "14:00",
    "14:50",
    "15:45",
    "16:35",
    "19:00",
    "19:55",
    "20:50",
  ];
  const endTimes = [
    "",
    "09:35",
    "10:25",
    "11:25",
    "12:15",
    "14:45",
    "15:35",
    "16:30",
    "17:20",
    "19:45",
    "20:40",
    "21:35",
  ];
  return `${startTimes[start] || "08:50"}–${endTimes[end] || "09:35"}`;
}
function formatWeeks(rule: PersonalCourse["slots"][number]["weeks"]) {
  return `${rule.start}–${rule.end}周${rule.mode === "odd" ? "单周" : rule.mode === "even" ? "双周" : ""}${rule.exclude.length ? `（除${rule.exclude.join("、")}周）` : ""}`;
}
